import { UserInputError } from "./errors-CEDWDXew.js";
import { ParamOrganizationSlug, ParamProjectSlugOrAll, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { assertProjectRefWithinConstraint } from "./project-constraints-DlfcU4mm.js";
import { formatIssueAlertRule, formatMetricAlertRule, resolveIssueAlertRule, resolveMetricAlertRule } from "./alerts-CVOIE5iC.js";
import { z } from "zod";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/get-alert-rule.ts
const AlertRuleKind = z.enum([
	"all",
	"issue",
	"metric"
]).describe("Which alert rule family to inspect. Use `issue` or `metric` for numeric IDs; `all` treats the value as an exact-name lookup.");
function describeMatch(match) {
	const project = match.projectSlug ? ` project ${match.projectSlug}` : "";
	return `${match.kind} alert ${String(match.rule.id)} (${match.rule.name})${project}`;
}
function assertMetricAlertRuleWithinProject(rule, projectSlug) {
	if (!projectSlug) return;
	if (!rule.projects?.includes(projectSlug)) throw new UserInputError(`Metric alert rule is outside project "${projectSlug}".`);
}
var get_alert_rule_default = defineTool({
	name: "get_alert_rule",
	skills: ["inspect"],
	requiredScopes: ["org:read", "project:read"],
	description: [
		"Get details for a Sentry alert rule.",
		"",
		"Use this tool when you need to inspect an alert rule's exact conditions, query, triggers, and actions before explaining or planning changes.",
		"",
		"<examples>",
		"get_alert_rule(organizationSlug='my-org', kind='metric', ruleIdOrName='12345')",
		"get_alert_rule(organizationSlug='my-org', kind='issue', projectSlug='backend', ruleIdOrName='Notify backend team')",
		"get_alert_rule(organizationSlug='my-org', projectSlug='backend', ruleIdOrName='P95 latency')",
		"</examples>",
		"",
		"<hints>",
		"- Use `kind='issue'` or `kind='metric'` for numeric IDs because issue and metric alerts use separate endpoints.",
		"- With `kind='all'`, a digit-only `ruleIdOrName` is treated as an exact alert rule name.",
		"- Issue alert rules are project-scoped, so `projectSlug` is required when `kind` is `issue`.",
		"</hints>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		regionUrl: ParamRegionUrl.nullable().default(null),
		kind: AlertRuleKind.default("all"),
		projectSlug: ParamProjectSlugOrAll.nullable().default(null),
		ruleIdOrName: z.string().trim().min(1).describe("The alert rule's numeric ID, or an exact alert rule name.")
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const requestedProjectSlug = params.projectSlug && params.projectSlug !== "all" ? params.projectSlug : void 0;
		if (requestedProjectSlug) assertProjectRefWithinConstraint({
			resourceLabel: "Alert rule",
			scopedProjectSlug: context.constraints.projectSlug,
			project: { slug: requestedProjectSlug }
		});
		const projectSlug = context.constraints.projectSlug ?? requestedProjectSlug;
		if (params.kind === "issue" && !projectSlug) throw new UserInputError("projectSlug is required when inspecting an issue alert rule.");
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		const organizationSlug = params.organizationSlug;
		setTag("organization.slug", organizationSlug);
		if (projectSlug) setTag("project.slug", projectSlug);
		let match;
		if (params.kind === "issue") {
			const rule = await resolveIssueAlertRule(apiService, {
				organizationSlug,
				projectSlug: projectSlug ?? "",
				ruleIdOrName: params.ruleIdOrName
			});
			match = {
				kind: "issue",
				rule,
				projectSlug: projectSlug ?? ""
			};
		} else if (params.kind === "metric") {
			const rule = await resolveMetricAlertRule(apiService, {
				organizationSlug,
				projectSlug,
				ruleIdOrName: params.ruleIdOrName
			});
			assertMetricAlertRuleWithinProject(rule, projectSlug);
			match = {
				kind: "metric",
				rule,
				projectSlug
			};
		} else {
			const matches = [];
			if (projectSlug) {
				const issueRules = await apiService.listIssueAlertRules({
					organizationSlug,
					projectSlug,
					query: params.ruleIdOrName,
					limit: 100
				});
				matches.push(...issueRules.filter((rule) => rule.name.toLowerCase() === params.ruleIdOrName.toLowerCase()).map((rule) => ({
					kind: "issue",
					rule,
					projectSlug
				})));
			}
			const metricRules = await apiService.listMetricAlertRules({
				organizationSlug,
				projectSlug,
				query: params.ruleIdOrName,
				limit: 100
			});
			matches.push(...metricRules.filter((rule) => rule.name.toLowerCase() === params.ruleIdOrName.toLowerCase()).map((rule) => ({
				kind: "metric",
				rule,
				projectSlug
			})));
			if (matches.length === 0) throw new UserInputError(`Alert rule "${params.ruleIdOrName}" was not found.`);
			if (matches.length > 1) throw new UserInputError(`Multiple alert rules named "${params.ruleIdOrName}" were found: ${matches.map(describeMatch).join(", ")}. Retry with the numeric rule ID and explicit kind.`);
			const [found] = matches;
			match = found.kind === "issue" ? {
				...found,
				rule: await apiService.getIssueAlertRule({
					organizationSlug,
					projectSlug: found.projectSlug,
					ruleId: found.rule.id
				})
			} : {
				...found,
				rule: await apiService.getMetricAlertRule({
					organizationSlug,
					ruleId: found.rule.id
				})
			};
			if (match.kind === "metric") assertMetricAlertRuleWithinProject(match.rule, match.projectSlug);
		}
		const scopeLabel = projectSlug ? `${organizationSlug}/${projectSlug}` : organizationSlug;
		let output = `# Alert Rule in **${scopeLabel}**\n\n`;
		output += match.kind === "issue" ? formatIssueAlertRule(match.rule, match.projectSlug, { url: apiService.getIssueAlertRuleUrl(organizationSlug, match.rule.id) }) : formatMetricAlertRule(match.rule, { url: apiService.getMetricAlertRuleUrl(organizationSlug, match.rule.id) });
		output += "\n\n## Response Notes\n\n";
		output += "- Use these details to inspect alert conditions, filters, routing, and notification actions before changing the rule in Sentry.\n";
		return output;
	}
});

//#endregion
export { get_alert_rule_default };
//# sourceMappingURL=get-alert-rule-WfTdEa_v.js.map